import { createContext, useContext, useLayoutEffect, useMemo, type ReactNode } from 'react';
import { useAppUserDefaultStore } from '../stores/appUserDefaultStore';

type Restaurant = ReturnType<typeof useAppUserDefaultStore.getState>['restaurant'];
const RestaurantViewContext = createContext<{
  restaurant: Restaurant;
  isCurrent: (restaurantId: string | undefined) => boolean;
} | null>(null);

/** A page-local drill-down. The navbar selection and saved preference stay unchanged. */
export function RestaurantViewScope({ restaurant, navbarRestaurantId, children }: {
  restaurant: Restaurant; navbarRestaurantId: string | undefined; children: ReactNode;
}) {
  const scope = useMemo(() => {
    const value = { restaurant, active: false, isCurrent: (id: string | undefined): boolean =>
      value.active && value.restaurant?.id === id && useAppUserDefaultStore.getState().restaurant?.id === navbarRestaurantId };
    return value;
  }, [restaurant, navbarRestaurantId]);
  useLayoutEffect(() => { scope.active = true; return () => { scope.active = false; }; }, [scope]);
  return <RestaurantViewContext.Provider value={scope}>{children}</RestaurantViewContext.Provider>;
}

export function useScopedRestaurant() {
  const selected = useAppUserDefaultStore(state => state.restaurant);
  const scope = useContext(RestaurantViewContext);
  return scope ? scope.restaurant : selected;
}

const isNavbarRestaurantCurrent = (id: string | undefined) => useAppUserDefaultStore.getState().restaurant?.id === id;
export function useRestaurantScopeCurrent() {
  return useContext(RestaurantViewContext)?.isCurrent ?? isNavbarRestaurantCurrent;
}
